import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { imagepathReturn } from "../../config/formatters.ts";

interface Alumni {
  id: number;
  name: string;
  photo: string;
  batch: string;
  job: string;
  highlight: string;
  wordings: string;
  sort_order: number;
  status: boolean;
}

export default function Alumni() {
  const navigate = useNavigate();
  const [alumni, setAlumni] = useState<Alumni[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAlumni();
  }, [page, limit, search]);

  const getAlumni = async () => {
    setLoading(true);
    const response = await request.post("alumni", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setAlumni(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "photo",
      label: "Photo",
      minWidth: 80,
      format: (value) => (
        <Box
          component="img"
          src={imagepathReturn(value)}
          sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: "50%" }}
        />
      ),
    },
    {
      id: "name",
      label: "Name",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "batch",
      label: "Batch",
      minWidth: 100,
      format: (value) => value,
    },
    {
      id: "job",
      label: "Job",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "highlight",
      label: "Highlight",
      minWidth: 150,
      format: (value) => value || "—",
    },
    {
      id: "wordings",
      label: "Wordings",
      minWidth: 200,
      format: (value) => value || "—",
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/alumni/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.name }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Alumni"
        showSearch
        searchPlaceholder="Search alumni..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Alumni",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/alumni/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={alumni}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Alumni - " + deleteDialog?.title}
          message="Are you sure you want to delete this alumni? This action cannot be undone."
          apiEndpoint={"alumni/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getAlumni();
          }}
        />
      </PageWrapper>
    </>
  );
}